import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
// API
import {
  ACCEPT_VIDEO_EDIT_SUBMISSIONS,
  REJECT_AND_SEND_BACK_BY_STORE,
} from 'api/mediaPost/mutations';
import {
  GET_MEDIA_POSTS,
  GET_VIDEO_REQUESTS_FOR_STORE,
} from 'api/mediaPost/queries';
import { CREATE_UNREGISTERED_CONTACT } from 'api/mentions/mutations';
// Types
import {
  CreateUnregisteredContact,
  CreateUnregisteredContactVariables,
} from 'api/mentions/types/CreateUnregisteredContact';
import {
  MediaPostStatus,
  StoreStatus,
  VideoEditRequestSubmissionStatus,
} from 'api/graphql-global-types';
import {
  GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos,
  GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_hashtags,
  GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_mentions,
  GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_reviewSubmissions,
} from 'api/mediaPost/types/GetVideoEditRequestsForStore';
import {
  AcceptVideoEditSubmissions,
  AcceptVideoEditSubmissionsVariables,
} from 'api/mediaPost/types/AcceptVideoEditSubmissions';
import {
  RejectAndSendBackByStore,
  RejectAndSendBackByStoreVariables,
} from 'api/mediaPost/types/RejectAndSendBackByStore';
// Helpers
import { formatMentionsInput } from 'helpers/mentions';
// Components
import VideoFeedbackModal from '../VideoFeedbackModal/VideoFeedbackModal';
import { showToast } from 'components/common/Toast/Toast';
// Ui
import VideoPlayer from 'ui/VideoPlayer/VideoPlayer';
import Icon from 'ui/Icon/Icon';
// Ui3
import Button from 'ui3/Button/Button';
import Text from 'ui3/Text/Text';
import Mentions from 'ui3/Mentions/Mentions';
import TagsInput from 'ui3/TagsInput/TagsInput';
// Styles
import styles from './SingleVideoCard.module.scss';

type SingleVideoCardProps = {
  storeId: string;
  videoItem: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos;
  lastReview?: boolean;
  onRejected: () => void;
  onChange: (videoItem) => void;
  handleCloseAll: () => void;
  clipIndex?: number;
  onEditViewChange?: (isOpen: boolean) => void;
};

interface VideoItemInterface {
  title: string | null;
  body: string | null;
  id: string;
  videoEditRequestId: string;
  mentions: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_mentions[];
  hashtags: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_hashtags[];
  formattedHashtags: string[];
  key: string;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  reviewSubmissions: GetVideoEditRequestsForStore_getVideoEditRequestsForStore_entities_editedVideos_reviewSubmissions[];
}

// every submitted video of a video request is displayed by this component
const SingleVideoCard = ({
  storeId,
  videoItem,
  lastReview,
  handleCloseAll,
  onRejected,
  onChange,
  clipIndex = 1,
  onEditViewChange,
}: SingleVideoCardProps): JSX.Element | null => {
  const {
    title,
    id,
    videoEditRequestId,
    videoUrl,
    thumbnailUrl,
    reviewSubmissions,
    body,
  } = videoItem;

  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState<boolean>(
    false
  );
  const [isApproveModal, setIsApproveModal] = useState<boolean>(true);
  const [expandEditView, setExpandEditView] = useState<boolean>(false);
  const [status, setStatus] = useState<VideoEditRequestSubmissionStatus>(
    reviewSubmissions[0].status
  );
  const [videoItemValue, setVideoItemValue] = useState<VideoItemInterface>({
    ...videoItem,
    formattedHashtags: videoItem.hashtags.map((tag) => tag.name) || [],
  });

  useEffect(() => {
    const formattedVideoItemRecord = {
      ...videoItem,
      formattedHashtags: videoItem.hashtags.map((tag) => tag.name) || [],
    };
    setVideoItemValue(formattedVideoItemRecord);
  }, [videoItem]);

  const showButtons = status === VideoEditRequestSubmissionStatus.Pending;

  const [acceptVideoEditSubmissions, { loading: acceptLoading }] = useMutation<
    AcceptVideoEditSubmissions,
    AcceptVideoEditSubmissionsVariables
  >(ACCEPT_VIDEO_EDIT_SUBMISSIONS);

  const [createUnregisteredContact] = useMutation<
    CreateUnregisteredContact,
    CreateUnregisteredContactVariables
  >(CREATE_UNREGISTERED_CONTACT);

  const [rejectVideoRequest, { loading: rejectLoading }] = useMutation<
    RejectAndSendBackByStore,
    RejectAndSendBackByStoreVariables
  >(REJECT_AND_SEND_BACK_BY_STORE);

  const isLoading = acceptLoading || rejectLoading;

  const handleOpenApproveVideoModal = () => {
    setIsApproveModal(true);
    setIsFeedbackModalOpen(true);
  };

  const handleOpenRejectVideoModal = () => {
    setIsApproveModal(false);
    setIsFeedbackModalOpen(true);
  };

  const refetchGetVideosQueryInput = {
    query: GET_VIDEO_REQUESTS_FOR_STORE,
    variables: {
      input: {
        storeId: storeId,
      },
    },
  };

  const refetchGetMediaFeedQueryInput = {
    query: GET_MEDIA_POSTS,
    variables: {
      input: {
        storeId,
        storeStatuses: [StoreStatus.Active, StoreStatus.Pending],
        statuses: [
          MediaPostStatus.published,
          MediaPostStatus.scheduled,
          MediaPostStatus.scheduledInstant,
        ],
      },
    },
  };

  const handleSendFeedback = async (
    feedback: string,
    rating?: number | null
  ) => {
    if (isApproveModal) {
      const formatMentionsForEditedVideo = async (mentionsValues) => {
        const createUnregisteredContactInput =
          mentionsValues?.filter((mention) => !mention.targetType) || [];

        const unregisterContactsData = await createUnregisteredContact({
          variables: {
            input: createUnregisteredContactInput,
          },
        });

        const newUnregisteredContacts =
          unregisterContactsData.data?.createUnregisteredContact?.map(
            (contact) => ({
              unregisteredContactId: contact.id,
            })
          ) || [];

        const oldMentions =
          mentionsValues?.filter((mention) => mention?.targetType) || [];
        const formatedOldMentions = formatMentionsInput(oldMentions);

        return [...formatedOldMentions, ...newUnregisteredContacts];
      };

      const formatHashtagsInput = (hashtags) => {
        if (hashtags?.length) {
          return hashtags.map((hashtag) => {
            // Check if hashtag is an object with a `name` property
            if (typeof hashtag === 'object' && 'name' in hashtag) {
              return { name: hashtag.name };
            }
            // Otherwise map it directly
            return { name: hashtag };
          });
        } else {
          return null;
        }
      };

      try {
        const submissionsInput =
          (await Promise.all([
            {
              id: videoItemValue.reviewSubmissions[0].id || '',
              hashtagInputs: formatHashtagsInput(videoItemValue.hashtags),
              mentionsInputs: await formatMentionsForEditedVideo(
                videoItemValue.mentions
              ),
            },
          ])) || [];

        await acceptVideoEditSubmissions({
          variables: {
            input: {
              feedback,
              rate: rating || 5,
              requestId: videoEditRequestId,
              submissions: submissionsInput,
            },
          },
          refetchQueries: [
            refetchGetVideosQueryInput,
            refetchGetMediaFeedQueryInput,
          ],
        });
        setStatus(VideoEditRequestSubmissionStatus.Approved);
        showToast({
          message: `You've successfully approved the video.`,
          type: 'success',
        });
        lastReview && handleCloseAll();
      } catch (error) {
        showToast({
          message: error?.message,
          type: 'error',
        });
      }
    } else {
      try {
        await rejectVideoRequest({
          variables: {
            input: {
              editedVideoId: id,
              feedback,
              storeId,
              videoEditRequestId,
            },
          },
          refetchQueries: [refetchGetVideosQueryInput],
        });
        setStatus(VideoEditRequestSubmissionStatus.Rejected);
        onRejected();
        showToast({
          message: `You've successfully rejected the video.`,
          type: 'success',
        });
        lastReview && handleCloseAll();
      } catch (error) {
        showToast({
          message: error?.message,
          type: 'error',
        });
      }
    }
    setIsFeedbackModalOpen(false);
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
  };

  const handleExpandEditView = () => {
    const newValue = !expandEditView;
    setExpandEditView(newValue);
    onEditViewChange?.(newValue);
  };

  const handleMentionChange = (mentions) => {
    const updatedEditedVideoItem = { ...videoItemValue, mentions };
    setVideoItemValue(updatedEditedVideoItem);
    onChange(updatedEditedVideoItem);
  };

  const handleChangeHashtags = (tags) => {
    const hashtags = tags.map((tag) => ({ name: tag })) || [];

    const updatedEditedVideoItem = {
      ...videoItemValue,
      hashtags,
      formattedHashtags: tags,
    };
    setVideoItemValue(updatedEditedVideoItem);
    onChange(updatedEditedVideoItem);
  };

  const handleSaveEdits = () => {
    setExpandEditView(false);
    onEditViewChange?.(false);
  };

  const showButtonsAndPlaceholders = () => {
    if (showButtons) {
      return (
        <div className={styles.buttonsContainer}>
          <div className={styles.editButtonWrapper}>
            <Button
              variant="secondary"
              onClick={handleExpandEditView}
              className={styles.editButton}
              size="medium"
              loading={isLoading}
              icon="pencil"
            >
              Edit
            </Button>
          </div>
          <div className={styles.buttonsWrapper}>
            <Button
              variant="success"
              onClick={handleOpenApproveVideoModal}
              className={styles.approveButton}
              size="medium"
              loading={isLoading}
              disabled={expandEditView}
            >
              Approve
            </Button>
            <Button
              variant="error"
              onClick={handleOpenRejectVideoModal}
              size="medium"
              className={styles.rejectButton}
              loading={isLoading}
              disabled={expandEditView}
            >
              Reject
            </Button>
          </div>
        </div>
      );
    } else {
      const isApproved =
        status === VideoEditRequestSubmissionStatus.AutoApproved ||
        status === VideoEditRequestSubmissionStatus.Approved;

      return (
        <div
          className={cn(styles.placeholderNotification, {
            [styles.accepted]: isApproved,
            [styles.rejected]: !isApproved,
          })}
        >
          <Icon
            name={isApproved ? 'check' : 'close'}
            className={styles.placeholderNotificationIcon}
          />

          {isApproved ? 'approved' : 'rejected'}
        </div>
      );
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.rootContainer}>
        <div className={styles.videoContainer}>
          <VideoPlayer
            wrapperClassName={styles.videoPlayerWrapper}
            videoClassName={styles.videoPlayer}
            autoPlay={false}
            src={videoUrl || ''}
            volumeControl="speaker"
            poster={thumbnailUrl || ''}
          />
        </div>
        <div className={styles.videoCardInfoAndButtons}>
          <div className={styles.videoCardInfo}>
            <div className={styles.clipTitle}>Video Clip #{clipIndex}</div>
            <div className={styles.titleSection}>
              <Text variant="subtitle2" color="lights-high">
                Title:
              </Text>
              <Text variant="body2Regular" color="lights-medium">
                {title || 'N/A'}
              </Text>
            </div>
            <div className={styles.descriptionSection}>
              <Text variant="subtitle2" color="lights-high">
                Description:
              </Text>
              <Text variant="body2Regular" color="lights-medium">
                {body || 'N/A'}
              </Text>
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            {showButtonsAndPlaceholders()}
          </div>
        </div>
      </div>
      {expandEditView && (
        <div className={styles.mentionContainer}>
          <TagsInput
            tags={videoItemValue.formattedHashtags}
            label="Tagging"
            onChange={handleChangeHashtags}
            name="formattedHashtags"
          />
          <Mentions
            mentions={videoItemValue.mentions}
            onChange={handleMentionChange}
          />
          <Button
            variant="success"
            className={styles.saveEditMention}
            onClick={handleSaveEdits}
          >
            Save edits
          </Button>
        </div>
      )}
      <VideoFeedbackModal
        isOpen={isFeedbackModalOpen}
        isApprove={isApproveModal}
        onClose={handleCloseFeedbackModal}
        sendFeedback={handleSendFeedback}
      />
    </div>
  );
};

export default SingleVideoCard;
